import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SubscriptionCheckResult, CachedSubscription } from '@/types/subscription';

const CACHE_VALIDITY_MINUTES = 1;
const DB_TIMEOUT_MS = 2000;
const EDGE_FUNCTION_TIMEOUT_MS = 10000;

export class SubscriptionService {
  /**
   * Fetches cached subscription data from database with timeout
   */
  static async getCachedSubscription(userId: string): Promise<CachedSubscription | null> {
    try {
      const dbPromise = supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Database query timeout')), DB_TIMEOUT_MS)
      );
      
      const result = await Promise.race([dbPromise, timeoutPromise]) as any;
      
      if (result.error) {
        console.error('[SUBSCRIPTION SERVICE] Database error:', result.error);
        return null;
      }
      
      return result.data;
    } catch (error) {
      console.error('[SUBSCRIPTION SERVICE] Failed to fetch cached data:', error);
      return null;
    }
  }

  /**
   * Checks if cached data is still valid (less than 5 minutes old)
   */
  static isCacheValid(cachedData: CachedSubscription): boolean {
    const lastChecked = new Date(cachedData.last_checked_at);
    const now = new Date();
    const minutesSinceCheck = (now.getTime() - lastChecked.getTime()) / (1000 * 60);
    
    return minutesSinceCheck < CACHE_VALIDITY_MINUTES;
  }

  /**
   * Fetches subscription data DIRECTLY from Stripe (not from DB)
   */
  static async fetchFreshSubscription(): Promise<SubscriptionCheckResult | null> {
    try {
      console.log('[SUBSCRIPTION SERVICE] Querying Stripe directly via check-stripe-direct');
      const edgeFunctionPromise = supabase.functions.invoke('check-stripe-direct');
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Edge function timeout')), EDGE_FUNCTION_TIMEOUT_MS)
      );
      
      const result = await Promise.race([edgeFunctionPromise, timeoutPromise]) as any;
      
      if (result.error) {
        console.error('[SUBSCRIPTION SERVICE] Edge function error:', result.error);
        return null;
      }
      
      console.log('[SUBSCRIPTION SERVICE] Direct Stripe response:', result.data);
      return result.data;
    } catch (error) {
      console.error('[SUBSCRIPTION SERVICE] Failed to fetch fresh data:', error);
      return null;
    }
  }

  /**
   * Main subscription check - queries database FIRST, then Stripe if needed
   */
  static async checkSubscription(userId: string): Promise<SubscriptionCheckResult> {
    console.log('[SUBSCRIPTION SERVICE] Checking subscription for user:', userId);

    // 1. First, check database for local trial_end
    const cachedData = await this.getCachedSubscription(userId);
    
    if (cachedData?.trial_end) {
      const trialEndDate = new Date(cachedData.trial_end);
      const now = new Date();
      
      if (trialEndDate > now) {
        console.log('[SUBSCRIPTION SERVICE] Active trial found in database:', {
          trial_end: cachedData.trial_end,
          days_remaining: Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
        });
        
        return {
          subscribed: cachedData.subscribed || false,
          product_id: cachedData.product_id,
          subscription_end: cachedData.subscription_end,
          trial_end: cachedData.trial_end,
          cancel_at_period_end: cachedData.cancel_at_period_end || false,
          cancel_at: cachedData.cancel_at,
          stripe_customer_id: cachedData.stripe_customer_id,
        };
      }
    }

    // 2. If subscribed via Stripe, check Stripe directly
    if (cachedData?.subscribed) {
      console.log('[SUBSCRIPTION SERVICE] User has paid subscription, checking Stripe');
      const stripeData = await this.fetchFreshSubscription();
      
      if (stripeData) {
        console.log('[SUBSCRIPTION SERVICE] Got fresh data from Stripe:', stripeData);
        return {
          subscribed: stripeData.subscribed || false,
          product_id: stripeData.product_id,
          subscription_end: stripeData.subscription_end,
          trial_end: stripeData.trial_end,
          cancel_at_period_end: stripeData.cancel_at_period_end,
          cancel_at: stripeData.cancel_at,
          stripe_customer_id: stripeData.stripe_customer_id,
        };
      }
    }

    // 3. Fallback: Return database data or no access
    console.log('[SUBSCRIPTION SERVICE] No active subscription or trial');
    return {
      subscribed: cachedData?.subscribed || false,
      product_id: cachedData?.product_id || null,
      subscription_end: cachedData?.subscription_end || null,
      trial_end: cachedData?.trial_end || null,
      cancel_at_period_end: cachedData?.cancel_at_period_end || false,
      cancel_at: cachedData?.cancel_at || null,
      stripe_customer_id: cachedData?.stripe_customer_id || null,
    };
  }

  /**
   * Creates a Stripe checkout session
   */
  static async createCheckout(locale?: string): Promise<void> {
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { locale: locale || 'de' }
      });
      
      if (error) throw error;
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Fehler beim Öffnen der Checkout-Seite');
    }
  }

  /**
   * Opens Stripe customer portal
   */
  static async openCustomerPortal(locale?: string): Promise<void> {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { locale: locale || 'de' }
      });
      
      if (error) {
        // Check if it's the "no customer" error
        if (error.message?.includes('NO_CUSTOMER')) {
          toast.error('Du musst zuerst ein Abonnement abschließen, um das Kundenportal zu nutzen.');
          return;
        }
        throw error;
      }
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Fehler beim Öffnen des Kundenportals');
    }
  }
}
